import { ContextMenu } from "@iventis/components";
import { Node } from "@iventis/domain-model/model/node";
import { useTreeBrowser, TreeBrowserDomainNodeContext, findNode } from "@iventis/tree-browser";
import { isAdmin, useFunctionality, useReadWritePermissionCheck } from "@iventis/utilities";
import React, { FunctionComponent, MouseEvent, useMemo } from "react";
import { useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { Theme } from "@emotion/react";
import { DataField } from "@iventis/domain-model/model/dataField";
import { useDataFieldServices } from "@iventis/datafield-editor";
import { MappingEngine } from "@iventis/map-engine/src/types/store-schema";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { UserScope } from "@iventis/domain-model/model/userScope";
import { setViewByObject, setViewByDataField } from "./sidebar.helpers";
import { RootState } from "./state/root.store";
import {
    createViewByAction,
    createShowLayerInformationAction,
    createShowLayerPropertiesAction,
    createShowAttributeTableAction,
    createShowPermissionsAction,
    createDeleteAction,
    createCopyAction,
    createExportAction,
    createImportGeojsonAction,
    createAddDataFieldsToMultipleLayersAction,
    moveLayerToFrontOfMap,
    moveLayerToBackOfMap,
    createToggleAllLayersVisibilityAction,
    SidebarContextMenuActionId,
} from "./sidebar-context-menu-actions";
import { canNodesBeCopied } from "./sidebar-toolbar-helpers";

export interface ContextMenuActions {
    showLayerInfo: () => void;
    selectNode: (e: MouseEvent<HTMLElement>) => void;
    showDeleteConfirmation: () => void;
    showLayerProperties: () => void;
    copyLayer: () => void;
    close: () => void;
    exportLayers: (exportType: "geojson" | "kml" | "dwg") => void;
    importGeojson: () => void;
    addAttributes: () => void;
    moveLayerToBackOfMap: () => void;
    moveLayerToFrontOfMap: () => void;
    toggleAllLayersVisibility: () => void;
    upgradePlan: { open: (feature: string) => void };
}

interface SidebarContextMenuProps {
    anchorElement: HTMLElement;
    node: Node;
    open: boolean;
    actions: ContextMenuActions;
}

/** Context menu for nodes within the sidebar (currently only supports layers) */
export const SidebarContextMenu: FunctionComponent<SidebarContextMenuProps> = ({ anchorElement, node, actions, open }) => {
    const { mapLayers, isExternalUser, mapId, mappingEngine } = useSelector((s: RootState) => ({
        mapLayers: s.mapReducer.mapModule.layers,
        isExternalUser: s.auth.user.userScope === UserScope.External,
        mapId: s.mapReducer.mapModule.mapId,
        mappingEngine: s.mapReducer.mappingEngine,
    }));

    const allUserLayersVisible = useMemo(() => mapLayers.value.filter((l) => l.remote).every((l) => l.visible), [mapLayers.stamp]);

    const { dataFieldListItemsService } = useDataFieldServices();

    const theme = useTheme<Theme>();

    const [{ tree, selectedNodeIds }, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext, node.id, "tree", "selectedNodeIds");

    const [canRead, canWrite] = useReadWritePermissionCheck(node.authorisation);

    const isAdminOnNode = isAdmin(node.authorisation);

    const mapNode: Node = tree ? (findNode([tree], mapId) as Node) : undefined;

    const isAdminOnMap = isAdmin(mapNode?.authorisation);

    const mapLayer = useMemo(() => mapLayers.value.find((l) => l.id === node.id), [node.id, mapLayers.stamp]);

    const handleDataFieldClicked = (dataField: DataField) => {
        setViewByDataField(dataField, mapLayer, treeBrowserContext, tree, dataFieldListItemsService);
        actions.close();
    };

    const handleViewByObjectClicked = () => {
        setViewByObject(mapLayer.id, treeBrowserContext, tree);
        actions.close();
    };

    const functionality = useFunctionality();

    const engineAgnosticActions: SidebarContextMenuActionId[] = [
        "VIEW_BY",
        "VIEW_BY_ATTRIBUTES",
        "SHOW_LAYER_INFORMATION",
        "SHOW_ATTRIBUTE_TABLE",
        "SHOW_PERMISSIONS",
        "EXPORT",
        "TOGGLE_ALL_LAYERS_VISIBILITY",
    ];

    const engineSpecificActionFilter: Record<MappingEngine, SidebarContextMenuActionId[]> = {
        [MappingEngine.Mapbox]: [
            ...engineAgnosticActions,
            "ADD_DATA_FIELDS",
            "IMPORT_GEOJSON",
            "COPY",
            "DELETE",
            "MOVE_LAYER_TO_BACK",
            "MOVE_LAYER_TO_FRONT",
            "SHOW_LAYER_PROPERTIES",
        ],
        [MappingEngine.Unreal]: [...engineAgnosticActions],
    };

    const nodeTypeAgnosticActions: SidebarContextMenuActionId[] = ["COPY", "DELETE"];

    const nodeTypeActionFilter: Partial<Record<NodeType, SidebarContextMenuActionId[]>> = {
        [NodeType.Layer]: [
            ...nodeTypeAgnosticActions,
            "VIEW_BY",
            "VIEW_BY_ATTRIBUTES",
            "SHOW_LAYER_INFORMATION",
            "SHOW_ATTRIBUTE_TABLE",
            "SHOW_PERMISSIONS",
            "EXPORT",
            "ADD_DATA_FIELDS",
            "IMPORT_GEOJSON",
            "COPY",
            "DELETE",
            "MOVE_LAYER_TO_BACK",
            "MOVE_LAYER_TO_FRONT",
            "SHOW_LAYER_PROPERTIES",
            "TOGGLE_ALL_LAYERS_VISIBILITY",
        ],
        [NodeType.Folder]: [...nodeTypeAgnosticActions],
    };

    const quantityAgnosticNodeActions: SidebarContextMenuActionId[] = ["SHOW_ATTRIBUTE_TABLE", "COPY", "EXPORT", "DELETE"];
    const singleNodeActions: SidebarContextMenuActionId[] = [
        ...quantityAgnosticNodeActions,
        "VIEW_BY",
        "VIEW_BY_ATTRIBUTES",
        "SHOW_LAYER_INFORMATION",
        "SHOW_LAYER_PROPERTIES",
        "SHOW_PERMISSIONS",
        "IMPORT_GEOJSON",
        "MOVE_LAYER_TO_BACK",
        "MOVE_LAYER_TO_FRONT",
        "TOGGLE_ALL_LAYERS_VISIBILITY",
    ];
    const multiNodeActions: SidebarContextMenuActionId[] = [...quantityAgnosticNodeActions, "ADD_DATA_FIELDS"];

    const interactableSelectedNodeIds = selectedNodeIds.filter((id) => {
        const selectedNode = findNode([tree], id) as Node;
        return selectedNode && (selectedNode.type === NodeType.Layer || selectedNode.type === NodeType.Folder);
    });

    const nodeTypesSelected: NodeType[] = [];

    interactableSelectedNodeIds.forEach((id) => {
        const selectedNode = findNode([tree], id) as Node;
        nodeTypesSelected.push(selectedNode.type);
    });

    return (
        <ContextMenu
            open={open}
            anchorEl={anchorElement}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            testId="sidebar-context-menu"
            actions={[
                createViewByAction(mapLayer, handleViewByObjectClicked, handleDataFieldClicked),
                createShowLayerInformationAction(!canRead, actions),
                createShowLayerPropertiesAction(!canWrite, node.id, actions),
                moveLayerToFrontOfMap(actions, !isAdminOnMap),
                moveLayerToBackOfMap(actions, !isAdminOnMap),
                createShowAttributeTableAction(interactableSelectedNodeIds, node.id, actions),
                createShowPermissionsAction(actions, !isAdminOnNode),
                createCopyAction(actions, canNodesBeCopied(selectedNodeIds, mapNode), nodeTypesSelected),
                createImportGeojsonAction(!canWrite, actions),
                createExportAction(
                    isExternalUser || !functionality.attributeDataExport,
                    isExternalUser || !functionality.mapObjectDwgExport,
                    isExternalUser || !functionality.mapObjectKmlExport,
                    actions
                ),
                createAddDataFieldsToMultipleLayersAction(actions),
                createToggleAllLayersVisibilityAction(allUserLayersVisible, actions),
                createDeleteAction(!isAdminOnNode && !mapLayer?.imported, theme.typographyColors.primary, actions),
            ]
                .filter((action) => engineSpecificActionFilter[mappingEngine].includes(action.contextMenuActionId))
                .filter((action) => nodeTypeActionFilter[node.type].includes(action.contextMenuActionId))
                .filter((action) =>
                    interactableSelectedNodeIds.length === 1 ? singleNodeActions.includes(action.contextMenuActionId) : multiNodeActions.includes(action.contextMenuActionId)
                )}
            space={44}
            onClose={actions.close}
        />
    );
};
